import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../App';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft, Clock, CheckCircle, XCircle, Loader2, DollarSign,
  Repeat, AlertCircle, ArrowRight, CreditCard, Smartphone, Wallet,
  RefreshCw, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending: { label: 'En attente', color: 'bg-amber-500/10 text-amber-400 border-amber-500/20', icon: Clock },
  validated: { label: 'Validé', color: 'bg-primary/10 text-primary border-primary/20', icon: CheckCircle },
  payment_pending: { label: 'Paiement attendu', color: 'bg-orange-500/10 text-orange-400 border-orange-500/20', icon: Wallet },
  paid: { label: 'Payé', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', icon: CheckCircle },
  completed: { label: 'Complété', color: 'bg-green-500/10 text-green-400 border-green-500/20', icon: CheckCircle },
  cancelled: { label: 'Annulé', color: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20', icon: XCircle },
  rejected: { label: 'Refusé', color: 'bg-red-500/10 text-red-400 border-red-500/20', icon: XCircle },
};

const PAY_LABELS = {
  interac: 'Virement Interac',
  orange_money: 'Orange Money',
  mtn_momo: 'MTN MoMo',
  cash: 'Espèces',
};

const RECEIVE_LABELS = {
  interac_email: 'Interac (courriel)',
  interac_phone: 'Interac (téléphone)',
  orange_money: 'Orange Money',
  mtn_momo: 'MTN MoMo',
  cash: 'Espèces',
};

const TABS = [
  { id: 'all', label: 'Toutes' },
  { id: 'pending', label: 'En attente', statuses: ['pending', 'validated', 'payment_pending'] },
  { id: 'active', label: 'En cours', statuses: ['paid'] },
  { id: 'completed', label: 'Complétées', statuses: ['completed'] },
  { id: 'cancelled', label: 'Annulées', statuses: ['cancelled', 'rejected'] },
];

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-CA', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// ─── ORDER CARD ──────────────────────────────────────────────────────
const OrderCard = ({ order, onCancel }) => {
  const [expanded, setExpanded] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const status = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
  const StatusIcon = status.icon;
  const isBuy = order.order_type === 'buy_cad';
  const canCancel = ['pending', 'validated'].includes(order.status);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await onCancel(order.id);
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div
      id={`exchange-order-${order.id}`}
      className="bg-muted/30 border border-border rounded-xl overflow-hidden transition-all hover:border-white/[0.12]"
      data-testid={`order-card-${order.id}`}
    >
      {/* Header */}
      <button
        className="w-full p-4 sm:p-5 flex items-center gap-4 text-left"
        onClick={() => setExpanded(!expanded)}
        data-testid={`order-toggle-${order.id}`}
      >
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isBuy ? 'bg-emerald-500/10' : 'bg-primary/10'}`}>
          {isBuy
            ? <DollarSign className="w-5 h-5 text-emerald-400" />
            : <Repeat className="w-5 h-5 text-primary" />
          }
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-heading font-semibold text-foreground text-sm">
              {isBuy ? 'Achat' : 'Vente'} de {order.amount_cad} CAD
            </span>
            <Badge className={`text-[10px] px-2 py-0.5 border ${status.color}`} data-testid={`order-status-${order.id}`}>
              {status.label}
            </Badge>
          </div>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span>{order.amount_xaf?.toLocaleString('fr-FR')} XAF</span>
            <span>@ {order.rate_applied} XAF/CAD</span>
            <span>{formatDate(order.created_at)}</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {canCancel && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10"
              onClick={(e) => { e.stopPropagation(); handleCancel(); }}
              disabled={cancelling}
              data-testid={`cancel-order-${order.id}`}
            >
              {cancelling ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Annuler'}
            </Button>
          )}
          {expanded
            ? <ChevronUp className="w-4 h-4 text-foreground/30" />
            : <ChevronDown className="w-4 h-4 text-foreground/30" />
          }
        </div>
      </button>

      {/* Expanded Details */}
      {expanded && (
        <div className="px-4 sm:px-5 pb-4 sm:pb-5 border-t border-border pt-4 space-y-4" data-testid={`order-details-${order.id}`}>
          {/* Summary Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-muted/40 rounded-lg p-3">
              <p className="text-[10px] text-muted-foreground uppercase mb-1">Type</p>
              <p className="text-sm font-semibold text-foreground">{isBuy ? 'Achat CAD' : 'Vente CAD'}</p>
            </div>
            <div className="bg-muted/40 rounded-lg p-3">
              <p className="text-[10px] text-muted-foreground uppercase mb-1">Montant</p>
              <p className="text-sm font-semibold text-foreground">{order.amount_cad} CAD</p>
            </div>
            <div className="bg-muted/40 rounded-lg p-3">
              <p className="text-[10px] text-muted-foreground uppercase mb-1">{isBuy ? 'Total payé' : 'Montant reçu'}</p>
              <p className="text-sm font-semibold text-[#D4AF37]">{order.amount_xaf?.toLocaleString('fr-FR')} XAF</p>
            </div>
            <div className="bg-muted/40 rounded-lg p-3">
              <p className="text-[10px] text-muted-foreground uppercase mb-1">Taux</p>
              <p className="text-sm font-semibold text-foreground">{order.rate_applied} XAF/CAD</p>
            </div>
          </div>

          {/* Payment & Receive Methods */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="bg-muted/40 rounded-lg p-3">
              <p className="text-[10px] text-muted-foreground uppercase mb-1 flex items-center gap-1">
                <CreditCard className="w-3 h-3" /> Mode de paiement
              </p>
              <p className="text-sm text-foreground">{PAY_LABELS[order.pay_method] || order.pay_method}</p>
            </div>
            <div className="bg-muted/40 rounded-lg p-3">
              <p className="text-[10px] text-muted-foreground uppercase mb-1 flex items-center gap-1">
                <Wallet className="w-3 h-3" /> Mode de réception
              </p>
              <p className="text-sm text-foreground">{RECEIVE_LABELS[order.receive_method] || order.receive_method}</p>
              {order.receive_name && <p className="text-xs text-muted-foreground mt-0.5">{order.receive_name}</p>}
            </div>
          </div>

          {/* Status Timeline */}
          {order.status_history && order.status_history.length > 0 && (
            <div>
              <p className="text-[10px] text-muted-foreground uppercase mb-2">Historique</p>
              <div className="space-y-2">
                {order.status_history.map((entry, idx) => {
                  const entryStatus = STATUS_CONFIG[entry.status] || STATUS_CONFIG.pending;
                  const EntryIcon = entryStatus.icon;
                  return (
                    <div key={idx} className="flex items-start gap-3 text-sm">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${entryStatus.color}`}>
                        <EntryIcon className="w-3 h-3" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-foreground/70">{entry.note || entryStatus.label}</p>
                        <p className="text-[11px] text-foreground/30">{formatDate(entry.timestamp)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── MAIN PAGE ───────────────────────────────────────────────────────
const MyExchangeTransactions = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const highlightOrderId = searchParams.get('order');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  const fetchOrders = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/exchange/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(res.data);
    } catch (err) {
      toast.error('Erreur lors du chargement des transactions');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleCancel = async (orderId) => {
    try {
      await axios.post(`${API}/exchange/orders/${orderId}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ordre annulé');
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'annulation');
    }
  };

  const filteredOrders = activeTab === 'all'
    ? orders
    : orders.filter(o => {
        const tab = TABS.find(t => t.id === activeTab);
        return tab?.statuses?.includes(o.status);
      });

  // Deep-link: scroll to and highlight specific order
  useEffect(() => {
    if (highlightOrderId && orders.length > 0) {
      const element = document.getElementById(`exchange-order-${highlightOrderId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.classList.add('ring-2', 'ring-[#D4AF37]', 'ring-offset-2');
        setTimeout(() => element.classList.remove('ring-2', 'ring-[#D4AF37]', 'ring-offset-2'), 3000);
      }
    }
  }, [highlightOrderId, orders]);

  // Stats
  const stats = {
    total: orders.length,
    pending: orders.filter(o => ['pending', 'validated', 'payment_pending'].includes(o.status)).length,
    active: orders.filter(o => o.status === 'paid').length,
    completed: orders.filter(o => o.status === 'completed').length,
    cancelled: orders.filter(o => ['cancelled', 'rejected'].includes(o.status)).length,
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-background" />
        <div className="absolute top-[-100px] right-[-50px] w-[500px] h-[500px] rounded-full bg-[#D4AF37]/[0.04] blur-[120px]" />

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-8">
          <button
            onClick={() => navigate('/currency/listings')}
            className="flex items-center gap-2 text-[#D4AF37]/70 hover:text-[#D4AF37] transition-colors mb-6 group"
            data-testid="back-to-exchange"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            <span className="text-sm tracking-wide uppercase">Échanger de l'argent</span>
          </button>

          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-1" data-testid="transactions-title">
                Mes transactions
              </h1>
              <p className="text-sm text-muted-foreground">Suivez vos échanges de devises CAD / XAF</p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchOrders}
                className="bg-muted/40 border-border text-muted-foreground rounded-xl"
                data-testid="refresh-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualiser
              </Button>
              <Button
                size="sm"
                onClick={() => navigate('/currency/listings')}
                className="bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold rounded-xl"
                data-testid="new-exchange-btn"
              >
                <DollarSign className="w-4 h-4 mr-2" />
                Nouvel échange
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="transaction-stats">
          {[
            { label: 'En attente', value: stats.pending, accent: 'text-amber-400' },
            { label: 'En cours', value: stats.active, accent: 'text-primary' },
            { label: 'Complétées', value: stats.completed, accent: 'text-green-400' },
            { label: 'Annulées', value: stats.cancelled, accent: 'text-zinc-400' },
          ].map(s => (
            <div key={s.label} className="bg-muted/30 border border-border rounded-xl p-4">
              <p className="text-[11px] text-muted-foreground uppercase tracking-wider mb-1">{s.label}</p>
              <p className={`font-heading text-2xl font-bold ${s.accent}`}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-muted/40 border border-border rounded-xl p-1 mb-6 overflow-x-auto" data-testid="transaction-tabs">
          {TABS.map(tab => (
            <button
              key={tab.id}
              className={`flex-1 min-w-[80px] py-2.5 px-3 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-[#D4AF37] text-black shadow-lg shadow-[#D4AF37]/20'
                  : 'text-muted-foreground hover:text-foreground/70'
              }`}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
            >
              {tab.label}
              {tab.id !== 'all' && (
                <span className="ml-1 opacity-70">
                  ({tab.id === 'pending' ? stats.pending : tab.id === 'active' ? stats.active : tab.id === 'completed' ? stats.completed : stats.cancelled})
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Orders List */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="text-center py-16 bg-white/[0.02] border border-border rounded-2xl" data-testid="empty-state">
            <div className="w-16 h-16 rounded-full bg-muted/40 flex items-center justify-center mx-auto mb-4">
              <DollarSign className="w-8 h-8 text-foreground/20" />
            </div>
            <h3 className="font-heading font-semibold text-foreground mb-2">Aucune transaction</h3>
            <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
              {activeTab === 'all'
                ? "Vous n'avez pas encore effectué d'échange de devises."
                : `Aucune transaction ${TABS.find(t => t.id === activeTab)?.label.toLowerCase() || ''}.`
              }
            </p>
            <Button
              onClick={() => navigate('/currency/listings')}
              className="bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold rounded-xl"
              data-testid="cta-new-exchange"
            >
              <DollarSign className="w-4 h-4 mr-2" />
              Échanger de l'argent
            </Button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="orders-list">
            {filteredOrders.map(order => (
              <OrderCard key={order.id} order={order} onCancel={handleCancel} />
            ))}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
};

export default MyExchangeTransactions;
